// Search over a whole catalogue domain, in the client: a domain has about a
// hundred entries, all of them already loaded for the suggestions.

// Fewer characters find nothing: one letter matches half the list.
export const SEARCH_MIN_LENGTH = 2;

export interface SearchEntry {
  id: string;
  // Every name the entry goes by, e.g. its German and English title.
  texts: readonly (string | null | undefined)[];
}

// Case and accents do not matter: "kyoto" finds "Kyōto", "creme" finds "Crème brûlée".
export function normalizeForSearch(text: string): string {
  return text.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase().trim();
}

export function isSearchable(query: string): boolean {
  return normalizeForSearch(query).length >= SEARCH_MIN_LENGTH;
}

// Ids of the entries whose names contain the query: one named exactly so
// first, then the rest in catalogue order (most familiar first).
export function searchCatalogue(entries: readonly SearchEntry[], query: string, limit: number): string[] {
  if (!isSearchable(query)) return [];
  const needle = normalizeForSearch(query);
  const names = (entry: SearchEntry) => entry.texts.flatMap((text) => (text ? [normalizeForSearch(text)] : []));
  const hits = entries.filter((entry) => names(entry).some((name) => name.includes(needle)));
  const exact = hits.filter((entry) => names(entry).includes(needle));
  return [...exact, ...hits.filter((entry) => !exact.includes(entry))].slice(0, limit).map((entry) => entry.id);
}

// The entry named exactly as the query, case and accents aside; null if none is.
export function exactMatch(entries: readonly SearchEntry[], query: string): string | null {
  const needle = normalizeForSearch(query);
  if (!needle) return null;
  return entries.find((entry) => entry.texts.some((text) => text && normalizeForSearch(text) === needle))?.id ?? null;
}
